import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import {
  AdCreativeListItem,
  AdCreativeRequest,
  AdCreativesApiResponse,
  DeleteResult,
} from './types';
import { adCreativeKey } from './useGetAdCreative';
import { AD_CREATIVES_QUERY_KEY } from './useGetAdCreatives';

type CreateParams = { data: AdCreativeRequest };
type UpdateParams = { id: string; data: Partial<AdCreativeRequest> };
type DeleteParams = { ids: string[] };

/** Zapisuje nową kreację albo szablon w bibliotece. */
export const usePostAdCreative = (
  options?: UseMutationOptions<AdCreativeListItem, AxiosError, CreateParams>,
): UseMutationResult<AdCreativeListItem, AxiosError, CreateParams> => {
  const queryClient = useQueryClient();

  return useMutation<AdCreativeListItem, AxiosError, CreateParams>({
    mutationFn: async ({ data }) => {
      const res = await axiosInstance.post<AdCreativesApiResponse<AdCreativeListItem>>(
        '/v3-createAdCreative',
        data,
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: AD_CREATIVES_QUERY_KEY });
      options?.onSuccess?.(...args);
    },
  });
};

/** Nadpisuje zapisany wpis. Scena idzie w całości albo wcale. */
export const usePutAdCreative = (
  options?: UseMutationOptions<AdCreativeListItem, AxiosError, UpdateParams>,
): UseMutationResult<AdCreativeListItem, AxiosError, UpdateParams> => {
  const queryClient = useQueryClient();

  return useMutation<AdCreativeListItem, AxiosError, UpdateParams>({
    mutationFn: async ({ id, data }) => {
      const res = await axiosInstance.put<AdCreativesApiResponse<AdCreativeListItem>>(
        '/v3-updateAdCreative',
        { id, ...data },
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (result, variables, ...rest) => {
      queryClient.invalidateQueries({ queryKey: AD_CREATIVES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: adCreativeKey(variables.id) });
      options?.onSuccess?.(result, variables, ...rest);
    },
  });
};

/** Kasuje wpisy z biblioteki. Pliki w Bytescale zostają nietknięte. */
export const useDeleteAdCreatives = (
  options?: UseMutationOptions<DeleteResult, AxiosError, DeleteParams>,
): UseMutationResult<DeleteResult, AxiosError, DeleteParams> => {
  const queryClient = useQueryClient();

  return useMutation<DeleteResult, AxiosError, DeleteParams>({
    mutationFn: async ({ ids }) => {
      const res = await axiosInstance.delete<AdCreativesApiResponse<DeleteResult>>(
        '/v3-deleteAdCreatives',
        { data: { ids } },
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: AD_CREATIVES_QUERY_KEY });
      options?.onSuccess?.(...args);
    },
  });
};
